/**
 * GenericInteraction — fallback for any unrecognized DXCUserInteraction type.
 *
 * Renders fields dynamically from the interaction object.
 * Future-proofs against new Dexie Cloud interaction types.
 */

import { useState, type FormEvent } from "react"
import { type DXCUserInteraction, resolveText } from "dexie-cloud-addon"
import { useTranslation } from "react-i18next"
import { Button } from "@/app/components/ui/button"
import { Input } from "@/app/components/ui/input"
import { cn } from "@/shared/utils/general/cn"

interface GenericInteractionProps {
  fields: DXCUserInteraction["fields"]
  onSubmit: DXCUserInteraction["onSubmit"]
  onCancel?: DXCUserInteraction["onCancel"]
  alerts: DXCUserInteraction["alerts"]
  submitLabel?: string
  cancelLabel?: string | null
}

export function GenericInteraction({
  fields,
  onSubmit,
  onCancel,
  alerts,
  submitLabel,
  cancelLabel,
}: GenericInteractionProps) {
  const { t } = useTranslation()
  const [params, setParams] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    if (!onSubmit) return
    setSubmitting(true)
    onSubmit(params)
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8 shadow-lg">
        <h2 className="mb-4 text-center text-lg font-semibold">
          {t("Action required")}
        </h2>

        {/* Alerts */}
        {alerts.map((alert, i) => (
          <div
            key={i}
            className={cn(
              "mb-4 rounded-lg px-4 py-3 text-sm text-center",
              alert.type === "error" &&
                "bg-[var(--red-500)]/10 text-[var(--red-500)]",
              alert.type === "warning" &&
                "bg-[var(--yellow-500)]/10 text-[var(--yellow-500)]",
              alert.type === "info" && "bg-primary/10 text-primary",
            )}
          >
            {resolveText(alert)}
          </div>
        ))}

        {/* Dynamic fields */}
        <form onSubmit={handleSubmit}>
          {Object.entries(
            fields as Record<
              string,
              { type: string; label: string; placeholder: string }
            >,
          ).map(([fieldName, { type: fieldType, label, placeholder }]) => (
            <div key={fieldName} className="mb-4">
              <label
                htmlFor={fieldName}
                className="mb-1.5 block text-sm font-medium text-muted-foreground"
              >
                {label}
              </label>
              <Input
                id={fieldName}
                type={fieldType}
                name={fieldName}
                placeholder={placeholder}
                disabled={!onSubmit || submitting}
                value={params[fieldName] ?? ""}
                onChange={(e) =>
                  setParams((prev) => ({
                    ...prev,
                    [fieldName]: e.target.value.trim(),
                  }))
                }
                className="h-12 text-base"
              />
            </div>
          ))}

          {onSubmit && (
            <Button
              type="submit"
              variant="primary"
              disabled={!onSubmit || submitting}
              className="mt-2 h-12 w-full text-base"
            >
              {submitting ? "..." : (submitLabel ?? t("Continue"))}
            </Button>
          )}
        </form>

        {onCancel && cancelLabel !== null && (
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            className="mt-2 h-10 w-full text-sm"
          >
            {cancelLabel ?? t("Cancel")}
          </Button>
        )}
      </div>
    </div>
  )
}
